// =============================================================================
// TRIAL · the handoff palette, as one file you can delete.
//
// Daniel's "Status button redesign V1" (23 Sep 2026) ships its own token set.
// Christian's call after the hex-by-hex comparison: take his grounds and text
// ramp for BOTH themes, keep ours for red, and see how the deeper dark surface
// actually looks before committing.
//
// WHY IT LIVES HERE AND NOT IN THE VENDORED FILE
//
// `vendor/aerios-tokens.css` and `tokens/aerios.gen.ts` are the design system,
// vendored from `aeriosgmbh/design-system v3.0.0-rc.1` and check-gated in CI
// (D32: a retyped hex is a fork of the design system that nobody labelled as
// one). Editing either would be exactly that fork. So the trial sits in front
// of them as an OVERLAY, applied in `color.ts`, and the vendored values stay
// untouched underneath.
//
// ROLLBACK: revert the commit, or set `TRIAL_ACTIVE` to false. Nothing else in
// the codebase imports this file.
//
// WHAT IS DELIBERATELY *NOT* TAKEN FROM THE HANDOFF
//
//   red        His `#E5544B` is the value OUR tokens record as moved —
//              4.43:1 on ink-800, 4.02:1 on ink-700, below the AA floor
//              D24–D29 make a token constraint. `--status-critical` #F0655C
//              replaced it. Ours wins (Christian, 23 Sep).
//   stale      Grey, not red. Freshness is a state, not an error: a unit
//              offline for weeks is the normal case (UNIT-LOC-02).
//   accent     His light accent `#0A6F66` is the pre-D71 value; D71 moved the
//              light accent to `#0A6C6F` deliberately. Ours stays.
//   muted      His dark muted `#7C888E` is likewise pre-D71 (4.48:1 on
//              ink-800 — below the floor). Ours stays `--grey-500` #88959B.
//   font       Ours (Inter Variable), not Inter Tight.
//   icons      D80's stroke 1.5 / butt / miter, not his 1.75.
//
// So this overlay is the GROUNDS and the neutral ramp, which is the part
// Christian wanted to see. Every value below is Daniel's except where a
// comment says otherwise.
//
// The contrast suite beside this file re-measures every ratio on each run, so
// if one of these grounds drops a text pair below its floor, the build says so
// rather than shipping it. That is the point of trying it here.
// =============================================================================

/**
 * `as boolean`, deliberately, and it is the one line here that needs a note.
 *
 * Inferred as the literal `true`, every `?? vendored` fallback in `color.ts`
 * becomes provably dead and `no-unnecessary-condition` deletes the rollback
 * path. Written as an annotation instead, `no-inferrable-types` objects. The
 * assertion widens the type without either rule firing, which keeps flipping
 * this to `false` a genuine one-word rollback.
 */
export const TRIAL_ACTIVE = true as boolean;

export const trialPalette = {
  light: {
    /** His `--surface`. Ours is `--light-50` #F4F6F5 — his page is white. */
    surfaceDefault: "#FFFFFF",
    /** His `--surface-bar`: our page colour, demoted to the toolbar band. */
    surfaceBar: "#F4F6F5",
    /** Ours is `--light-panel` #EAEEED. */
    surfaceSunken: "#F4F6F5",
    /** The top of the ladder stays white in both palettes. */
    surfaceRaised: "#FFFFFF",

    /** Identical to ours (`--light-heading`). */
    textPrimary: "#07090B",
    /** His `--text-body`; ours derives #454C4F for this job. */
    textSecondary: "#3E474B",
    /** His `--text-muted`; identical to our `--light-body`. */
    textTertiary: "#5A6468",

    /** Identical to ours (`--light-border`). */
    border: "#D3DAD9",
  },
  dark: {
    /** His `--surface`. Ours is `--ink-900` #1A1A1A — his is much deeper. */
    surfaceDefault: "#0E1114",
    /** His `--surface-bar`. Ours is `--ink-800` #1F2022. */
    surfaceBar: "#07090B",
    /** His menu/tertiary ground; the step between bar and surface. */
    surfaceSunken: "#151A1D",
    /** His `--btn-secondary-bg`, the raised step. */
    surfaceRaised: "#1C2226",

    /** Identical to ours (`--white-50`). */
    textPrimary: "#EEF2F1",
    /** Identical to ours (`--grey-300`). */
    textSecondary: "#A7B2B7",
    /**
     * OURS, not his. He specifies #7C888E, which D71 moved off for measuring
     * 4.48:1 on ink-800 and 4.06:1 on ink-700 — below the AA floor.
     */
    textTertiary: "#88959B",

    /** His `--border`. Ours is `--line-600` #2E3134. */
    border: "#1C2226",
  },
} as const;

// ── The FilterChip's own tokens ─────────────────────────────────────────────
//
// Daniel's chip is a solid shape in BOTH states — rule 4 of the handoff, "no
// opacity for state": every tint is a solid token per theme, no rgba, no
// `opacity` on controls. Off is a neutral fill, on is ink, and the only thing
// the fill has to do is change.
//
// Rule 3 is the one that changes the component's meaning: **the dot is the
// status identity and keeps its colour whether the chip is on or off.** Only
// the fill moves. That is the opposite of what we shipped yesterday, where the
// dot emptied to a ring when the filter was off — there, the dot carried BOTH
// "which state" and "am I on", which is the doubling the handoff removes.
//
// `on` is ink rather than accent, which is the handoff's default; the accent
// pair is tokenised beside it and is one swap away if the ink reads too heavy.
export const trialChip = {
  light: {
    offBg: "#E6EBEA",
    offHover: "#DAE0DF",
    offPressed: "#C9D1D0",
    offFg: "#3E474B",
    offCount: "#5A6468",
    onBg: "#07090B",
    onHover: "#1F2629",
    onPressed: "#343D41",
    onFg: "#FFFFFF",
    onCount: "#A7B2B7",
    /** The alternative `on` pair, if ink reads too heavy. */
    onAccentBg: "#0A6C6F", // OURS, not his #0A6F66 — D71 moved off that value
    onAccentFg: "#FFFFFF",
    /** Count 0 — still visible, still clickable. */
    emptyBg: "#EEF2F1",
    emptyFg: "#A3ADB1",
    emptyDot: "#D3DAD9",
  },
  dark: {
    offBg: "#151A1D",
    offHover: "#1F262A",
    offPressed: "#2C3438",
    offFg: "#A7B2B7",
    offCount: "#8B979C",
    onBg: "#EEF2F1",
    onHover: "#D5DCDB",
    onPressed: "#BCC6C5",
    onFg: "#07090B",
    onCount: "#5A6468",
    onAccentBg: "#3FD9C7",
    onAccentFg: "#07090B",
    emptyBg: "#0E1114",
    emptyFg: "#4E585D",
    emptyDot: "#2A3237",
  },
} as const;

/**
 * The four freshness dots.
 *
 * Fresh and aging are Daniel's. Stale is NOT: his is red (#E5544B), which is
 * both the value our tokens record as failing AA and a claim the product does
 * not make — freshness is a state, not an error, and a unit offline for weeks
 * is the normal case (UNIT-LOC-02). Stale is grey, and `never` is the same
 * grey drawn as a hollow ring, so the two differ in SHAPE and survive a
 * greyscale screenshot.
 */
export const trialFreshnessDot = {
  light: {
    fresh: "#2E9E68",
    aging: "#D8961F",
    stale: "#8C979B",
    never: "#8C979B",
  },
  dark: {
    fresh: "#4CC38A",
    aging: "#E5A93C",
    stale: "#88959B", // ours; his #7C888E is the pre-D71 value
    never: "#88959B",
  },
} as const;

// ── The Button's own tokens ─────────────────────────────────────────────────
//
// The handoff's button variants are primary (`contained`), **secondary
// (`tonal`, and it is the DEFAULT)**, tertiary (`text`) and destructive. Tonal
// is the one that changes what we ship: today a secondary button is `outlined`
// — a hairline around a transparent middle — and Daniel's is a solid neutral
// fill with no border at all.
//
// The secondary fill is, deliberately, the SAME pair as the filter chip's off
// state (#E6EBEA / #DAE0DF / #C9D1D0). A chip at rest and a secondary button
// are both "a neutral control you may press", so they share a ground rather
// than disagreeing by a few percent — which is what the old tonal-alpha
// approach did.
//
// Disabled is solid, per rule 4: `--btn-disabled-bg` and `--btn-disabled-fg`
// rather than an opacity on the live colours. A dimmed control inherits the
// ground behind it and lands at an unpredictable contrast; a named pair does
// not.
//
// Only the secondary and disabled pairs are here, because only those two are
// used. Primary stays ours — his light primary #0A6F66 is the pre-D71 accent,
// and D71 moved the light accent to #0A6C6F on purpose.
export const trialButton = {
  light: {
    secondaryBg: "#E6EBEA",
    secondaryHover: "#DAE0DF",
    secondaryPressed: "#C9D1D0",
    secondaryFg: "#07090B",
    disabledBg: "#EAEEED",
    disabledFg: "#9AA4A8",
  },
  dark: {
    secondaryBg: "#1C2226",
    secondaryHover: "#252D31",
    secondaryPressed: "#30393E",
    secondaryFg: "#EEF2F1",
    disabledBg: "#151A1D",
    disabledFg: "#4E585D",
  },
} as const;
